import { ChevronDown } from 'lucide-react';
import { getCountry, COUNTRIES, flagEmoji, uiLangFor, uiStrings, hasBlog } from './countries.js';
import LanguageSwitcher from './LanguageSwitcher.jsx';
import { useLang } from './langStore.js';
import CountryTestCentres from './CountryTestCentres.jsx';

import { appStoreUrl, playStoreUrl } from './storeLinks.js';
import StickyDownloadBar from './StickyDownloadBar.jsx';

const PAGE_TYPE  = 'test-centres';
const APP_STORE  = appStoreUrl(PAGE_TYPE);
const PLAY_STORE = playStoreUrl(PAGE_TYPE);

// Every market, generated from countries.js. Every market home lives under
// /<cc> (GB included, at /gb); the bare root is the neutral global landing.
const SWITCHER = Object.values(COUNTRIES).map(c => ({
  f: flagEmoji(c.code),
  n: c.name,
  h: c.code === 'GB' ? '/gb' : `/${c.code.toLowerCase()}`,
}));

export default function CountryTestCentresShell({ country = 'GB', centres = [] }) {
  const c = getCountry(country);
  const cc = c.code.toLowerCase();
  const flag = flagEmoji(c.code);
  const year = 2026;
  // Chrome language: defaults to the country's language (deterministic, so
  // SSR/hydration match); a saved visitor preference applies after mount.
  const lang = useLang(uiLangFor(c.code));
  const t = uiStrings(lang);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="fixed top-0 inset-x-0 z-50 bg-white border-b border-gray-100 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href={`/${cc}`} className="flex items-center gap-3" aria-label="SteerClear home">
            <img src="/logo.png" alt="SteerClear Logo" width="48" height="48" className="w-12 h-12 object-contain" />
            <span className="font-bold text-xl tracking-tight text-gray-900">SteerClear&reg;</span>
          </a>
          <div className="flex items-center gap-3 md:gap-8">
            <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
              <a href={`/${cc}/test-centres`} className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors">{t.testCentres}</a>
              <a href={`/${cc}/practical-driving-test`} className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors">{t.practicalTest}</a>
              {hasBlog(cc) && <a href={`/${cc}/blog`} className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors">{t.blog}</a>}
            </nav>
            <LanguageSwitcher lang={lang} label={t.language} />
            <details className="relative">
              <summary className="list-none cursor-pointer flex items-center gap-1 text-sm font-medium text-gray-600 border border-gray-200 rounded-full px-3 py-1.5 hover:border-gray-400 [&::-webkit-details-marker]:hidden">
                {flag} {c.code} <ChevronDown className="w-4 h-4" />
              </summary>
              <div className="absolute right-0 top-full pt-2 w-56 z-50">
                <div className="bg-white border border-gray-100 rounded-xl shadow-lg py-2 max-h-[60vh] overflow-y-auto">
                  {SWITCHER.map((s) => (
                    <a
                      key={s.h}
                      href={s.h}
                      onClick={(e) => { e.preventDefault(); window.location.assign(s.h); }}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <span>{s.f}</span> {s.n}
                    </a>
                  ))}
                </div>
              </div>
            </details>
            <div className="hidden sm:flex items-center gap-2" data-placement="header" data-store-pair="">
              <a href={APP_STORE} target="_blank" rel="noopener noreferrer" data-store="ios" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-black transition-colors whitespace-nowrap">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                App Store
              </a>
              <a href={PLAY_STORE} target="_blank" rel="noopener noreferrer" data-store="play" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-black transition-colors whitespace-nowrap">
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="#00B2FF" d="M3.5 1.9v20.2L13.2 12z"/><path fill="#00DD72" d="M3.5 1.9l13.4 7.8-3.7 2.3z"/><path fill="#FFC900" d="M16.9 9.7 20.9 12l-4 2.3-3.7-2.3z"/><path fill="#FF3A44" d="M3.5 22.1 13.2 12l3.7 2.3z"/></svg>
                Google Play
              </a>
            </div>
          </div>
        </div>
      </header>

      <CountryTestCentres country={country} centres={centres} />

      <footer className="py-12 border-t border-gray-100 bg-white text-sm text-gray-500">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <img src="/logo.png" alt="" width="28" height="28" className="w-7 h-7 object-contain" />
              <span className="font-bold text-gray-900">SteerClear&reg;</span>
            </div>
            <p className="max-w-sm">Practise the {c.name} test-centre routes with live scoring. On iOS and Android.</p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <a href={`/${cc}/test-centres`} className="hover:text-gray-900">{t.testCentres}</a>
            <a href={`/${cc}/practical-driving-test`} className="hover:text-gray-900">{t.practicalTest}</a>
            {hasBlog(cc) && <a href={`/${cc}/blog`} className="hover:text-gray-900">{t.blog}</a>}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-8 pt-6 border-t border-gray-100 text-xs">
          © {year} SteerClear Ltd. (also known as Steer Clear) · Company no. 16181182 ·{' '}
          <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=54682e08-2d40-4392-a427-97e47b45dad4" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900">Privacy</a> ·{' '}
          <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=d09c20af-72bf-43d7-bde4-95e544b02a5b" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900">Cookies</a>
        </div>
      </footer>

      <StickyDownloadBar pageType={PAGE_TYPE} lead={t.getFree} />
    </div>
  );
}
